import { useCallback, useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Spinner, StateBlock } from '../../../components/ui/Feedback';
import { api, errorMessage } from '../../../lib/api';
import { useAsync } from '../../../lib/hooks';
import { formatDate } from '../../../lib/format';
import { groupConversations, rejectedOrFailed } from '../../../lib/conversations';
import type { Conversation, TrailKind } from '../../../lib/conversations';
import type { RfpResponses } from '../../../types/api';
import { ConversationDrawer } from './ConversationDrawer';
import { RejectedEmailsPanel } from './RejectedEmailsPanel';

/** Direction badge for the last message in a conversation. */
function LastKindBadge({ kind }: { kind: TrailKind | null }) {
  if (kind === 'outbound') return <Badge variant="info">Sent</Badge>;
  if (kind === 'inbound') return <Badge variant="success">Received</Badge>;
  if (kind === 'auto-reply') return <Badge variant="orange">Auto-reply</Badge>;
  return null;
}

/**
 * The redesigned "Communications" panel: a conversation list plus the
 * rejected/failed emails, opening the selected thread in a right-side drawer.
 */
export function CommunicationPanel({ rfpId }: { rfpId: number }) {
  const loader = useCallback(() => api.get<RfpResponses>(`/rfps/${rfpId}/responses`), [rfpId]);
  const { data, loading, error, reload } = useAsync(loader, [rfpId]);
  const [selected, setSelected] = useState<Conversation | null>(null);

  const conversations = groupConversations(data?.thread ?? [], data?.autoReplies ?? []);
  const rejected = rejectedOrFailed(data?.autoReplies ?? []);

  return (
    <section className="panel">
      <div className="panel__body">
        <h3 className="detail-section-title">Communications</h3>

        {loading && !data ? (
          <div className="state-block">
            <Spinner />
          </div>
        ) : null}

        {error && !data ? (
          <StateBlock
            title="Could not load communications"
            description={errorMessage(error)}
            action={
              <Button variant="outlined" onClick={reload}>
                Try again
              </Button>
            }
          />
        ) : null}

        {data ? (
          <>
            <p className="text-muted">
              {conversations.length} conversation(s) · {rejected.length} rejected/failed
            </p>

            {conversations.length === 0 && rejected.length === 0 ? (
              <StateBlock
                title="No communications yet"
                description="Send the RFP to vendors to start the conversation."
              />
            ) : (
              <>
                {conversations.length > 0 ? (
                  <div className="conversation-list">
                    {conversations.map((conversation) => (
                      <button
                        key={conversation.id}
                        type="button"
                        className="conversation-row"
                        onClick={() => setSelected(conversation)}
                      >
                        <div className="conversation-row__main">
                          <span>{conversation.vendorName ?? 'Unassigned'}</span>
                          {conversation.threadToken ? (
                            <span className="conversation-row__subject">
                              [{conversation.threadToken}]
                            </span>
                          ) : null}
                          {conversation.subject ? (
                            <span className="conversation-row__subject">{conversation.subject}</span>
                          ) : null}
                        </div>
                        <LastKindBadge kind={conversation.lastKind} />
                        <span className="conversation-row__meta">
                          {formatDate(conversation.latestAt)}
                        </span>
                        <span className="conversation-row__count">
                          {conversation.messageCount}{' '}
                          {conversation.messageCount === 1 ? 'message' : 'messages'}
                          {conversation.hasAttachment ? ' · attachment' : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}

                <RejectedEmailsPanel entries={rejected} />
              </>
            )}
          </>
        ) : null}
      </div>

      <ConversationDrawer
        conversation={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </section>
  );
}
